import { useState } from 'react';
import { 
  Users, 
  HeartHandshake, 
  ArrowRight, 
  Sparkles,
  Building2,
  Loader2,
  Award,
  ShieldCheck,
} from 'lucide-react';
import EventHQLogo from '../../../assets/event-hq-logo.png';
import { useAuth } from '../../contexts/AuthContext';
import type { PrimaryInterest } from '../../types/database';

interface InterestOption {
  id: PrimaryInterest;
  icon: React.ElementType;
  title: string;
  description: string;
  features: string[];
  gradient: string;
  iconBg: string;
}

const interests: InterestOption[] = [
  {
    id: 'organizing',
    icon: Building2,
    title: 'Run Tournaments & Leagues',
    description: 'I organize and manage competitive events and seasons',
    features: ['Create tournaments', 'Run leagues & seasons', 'Manage brackets', 'Track standings', 'Score matches live'],
    gradient: 'from-violet-500 to-purple-600',
    iconBg: 'bg-violet-100 text-violet-600',
  },
  {
    id: 'coaching',
    icon: Users,
    title: 'Coach a Team',
    description: 'I manage athletes and prepare them to compete',
    features: ['Track your roster', 'Register for events', 'Monitor athlete matches', 'Receive notifications'],
    gradient: 'from-amber-500 to-orange-600',
    iconBg: 'bg-amber-100 text-amber-600',
  },
  {
    id: 'parenting',
    icon: ShieldCheck,
    title: 'Parent / Guardian',
    description: "I manage my athlete's registrations and follow their progress",
    features: ['Register your athlete', 'Track their schedule', 'Follow matches live', 'View results & stats'],
    gradient: 'from-sky-500 to-blue-600',
    iconBg: 'bg-sky-100 text-sky-600',
  },
  {
    id: 'supporter',
    icon: HeartHandshake,
    title: 'Athletes & supporters',
    description: 'Follow what you care about, explore events, and register to compete whenever you want',
    features: [
      'Follow athletes, teams, and tournaments',
      'Discover events and sign up when you are ready',
      'Live match updates and results',
      'Watch streams and track schedules',
    ],
    gradient: 'from-emerald-500 via-teal-500 to-rose-500',
    iconBg: 'bg-teal-100 text-teal-600',
  },
];

export function OnboardingModal() {
  const { profile, completeOnboarding } = useAuth();
  const [selected, setSelected] = useState<PrimaryInterest | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'welcome' | 'interest'>('welcome');

  const handleContinue = async () => {
    if (!selected) return;
    
    setLoading(true);
    const { error } = await completeOnboarding(selected);
    
    if (error) {
      console.error('Error completing onboarding:', error);
    }
    setLoading(false);
  };

  const handleSkip = async () => {
    setLoading(true);
    const { error } = await completeOnboarding('supporter');
    if (error) {
      console.error('Error completing onboarding:', error);
    }
    setLoading(false);
  };

  const selectedInterest = interests.find(i => i.id === selected);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300 flex flex-col my-auto">
        {/* Decorative header gradient */}
        <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-br from-teal-500 via-cyan-500 to-blue-600 opacity-90" />
        
        {/* Content */}
        <div className="relative overflow-y-auto flex-1">
          {step === 'welcome' ? (
            <div className="text-center px-8 pt-12 pb-8">
              {/* Welcome Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-lg mb-6">
                <img src={EventHQLogo} alt="Event HQ" className="w-10 h-10 rounded-lg object-contain" />
              </div>
              
              <h1 className="text-3xl font-bold text-white mb-3">
                Welcome, {profile?.full_name?.split(' ')[0]}! 🎉
              </h1>
              <p className="text-lg text-white/90 mb-8 max-w-md mx-auto">
                Let's personalize your Podium HQ experience
              </p>
              
{/* Feature highlights */}
                <div className="bg-white rounded-2xl p-6 mb-6">
                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div className="p-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-violet-100 to-purple-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <Building2 className="w-5 h-5 text-violet-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-900">Tournaments</p>
                    </div>
                    <div className="p-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <Award className="w-5 h-5 text-teal-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-900">Leagues</p>
                    </div>
                    <div className="p-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <Users className="w-5 h-5 text-amber-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-900">Coach</p>
                    </div>
                    <div className="p-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-100 to-rose-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <HeartHandshake className="w-5 h-5 text-teal-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-900">Fans & athletes</p>
                    </div>
                  </div>
                </div>

              <button
                onClick={() => setStep('interest')}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all shadow-lg shadow-teal-500/25"
              >
                Get Started
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center px-8 pt-10 pb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4">
                  <Sparkles className="w-8 h-8 text-teal-600" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  What brings you here?
                </h2>
                <p className="text-white/80 text-sm">
                  This helps us customize your dashboard. You can do everything regardless of what you choose.
                </p>
              </div>

              {/* Options */}
              <div className="px-6 pb-6">
                <div className="bg-white rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {interests.map((interest) => {
                    const Icon = interest.icon;
                    const isSelected = selected === interest.id;
                    
                    return (
                      <button
                        key={interest.id}
                        onClick={() => setSelected(interest.id)}
                        className={`relative text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/20'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute top-3 right-3 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        
                        <div className={`w-10 h-10 rounded-lg ${interest.iconBg} flex items-center justify-center mb-3`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        
                        <h3 className={`font-semibold mb-1 ${isSelected ? 'text-teal-900' : 'text-gray-900'}`}>
                          {interest.title}
                        </h3>
                        <p className={`text-sm ${isSelected ? 'text-teal-700' : 'text-gray-500'}`}>
                          {interest.description}
                        </p>
                      </button>
                    );
                  })}
                </div>

                {/* Selected interest features */}
                {selectedInterest && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-100 animate-in fade-in slide-in-from-bottom-2 duration-200">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                      What you'll get
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedInterest.features.map((feature) => (
                        <span
                          key={feature}
                          className="inline-flex items-center gap-1 text-sm text-gray-700 bg-white px-3 py-1 rounded-full border border-gray-200"
                        >
                          <svg className="w-3.5 h-3.5 text-teal-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                  <button
                    onClick={handleSkip}
                    disabled={loading}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                  >
                    Skip for now
                  </button>
                  
                  <button
                    onClick={handleContinue}
                    disabled={!selected || loading}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-teal-500/25"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

